/**
 * Central persistence + bootstrap helpers for app dark mode (admin + student shells).
 *
 * Duplicated in a tiny inline script in `public/index.html` so `data-theme`
 * exists before the React bundle executes (first paint / FOUC). Keep paths in sync.
 */

export const ADMIN_THEME_LS_KEY = "edutopia_admin_theme";
export const ADMIN_THEME_SS_KEY = "edutopia_admin_theme";

export function readAdminThemePreference() {
  if (typeof window === "undefined") return "light";
  try {
    const ls = window.localStorage.getItem(ADMIN_THEME_LS_KEY);
    if (ls === "dark") return "dark";
    if (ls === "light") return "light";
    const ss = window.sessionStorage.getItem(ADMIN_THEME_SS_KEY);
    if (ss === "dark") return "dark";
    if (ss === "light") return "light";
  } catch {
    /* quota / disabled storage */
  }
  return "light";
}

export function writeAdminThemePreference(mode) {
  const normalized = mode === "dark" ? "dark" : "light";
  try {
    window.localStorage.setItem(ADMIN_THEME_LS_KEY, normalized);
    window.sessionStorage.setItem(ADMIN_THEME_SS_KEY, normalized);
  } catch {
    /* ignore */
  }
}

export function applyAdminThemeToHtml(mode) {
  if (typeof document === "undefined") return;
  if (mode === "dark") {
    document.documentElement.setAttribute("data-theme", "dark");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
}

/**
 * True when the URL corresponds to pages that use `Layout` / admin chrome.
 * Uses substring checks so nested deploy paths like `/app/dashboard/...` still work.
 */
export function isAdminShellLocation(pathname = "", hash = "") {
  const p = pathname || "";
  const h = hash || "";
  if (p.includes("/dashboard")) return true;
  if (p.includes("/super-admin/dashboard")) return true;
  if (p.includes("/scan-code")) return true;
  if (h && (h.includes("dashboard") || h.includes("super-admin"))) return true;
  return false;
}

/**
 * True when the URL is an authenticated student area (portal or legacy).
 * Uses prefix checks; avoids `/dashboard/students` via admin shell taking precedence.
 */
export function isStudentShellLocation(pathname = "") {
  const p = pathname || "";
  if (p.startsWith("/students")) return true;
  if (p.startsWith("/student/")) return true;
  if (p.startsWith("/model-answer")) return true;
  if (p.startsWith("/model-answers")) return true;
  if (p.startsWith("/homework/")) return true;
  if (p.startsWith("/homeworks/")) return true;
  if (p.startsWith("/exam")) return true;
  if (p.startsWith("/exams/")) return true;
  if (p.startsWith("/solve-exam")) return true;
  if (p.startsWith("/exam-guide")) return true;
  if (p.startsWith("/exam-result")) return true;
  if (p.startsWith("/questions")) return true;
  if (p.startsWith("/video/qr-code")) return true;
  if (p.startsWith("/videos/")) return true;
  if (p.startsWith("/asquera")) return true;
  if (p.startsWith("/study/")) return true;
  if (p.startsWith("/performance-report")) return true;
  return false;
}

/** Admin dashboard, teacher shell, or student UI — applies saved dark/light preference. */
export function isThemedAppShellLocation(pathname = "", hash = "") {
  return isAdminShellLocation(pathname, hash) || isStudentShellLocation(pathname);
}

/** Run synchronously from `src/index.js` before ReactDOM.createRoot. */
export function bootstrapAdminThemeBeforeReact() {
  if (typeof window === "undefined") return;
  try {
    const { pathname, hash } = window.location;
    if (!isThemedAppShellLocation(pathname, hash)) return;
    if (readAdminThemePreference() === "dark") {
      document.documentElement.setAttribute("data-theme", "dark");
    }
  } catch {
    /* ignore */
  }
}

/**
 * `localStorage.clear()` wipes the theme key; several auth paths call it.
 * Preserve preference across clears so reload / logout does not reset UI theme.
 */
export function preserveAdminThemeDuringClear(clearFn) {
  const saved = readAdminThemePreference();
  clearFn();
  writeAdminThemePreference(saved);
}
